import { Button, Dialog } from '@mui/material';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Currencies from '../../../../utils/Currencies';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';
import { getPriceBasis } from 'arbitrip-common/client/utils/DealPriceBasis';
import { buildPointsRecheckSplit } from 'arbitrip-common/client/utils/PointsRecheckSplit';

function TermsChangedDialog({
    modalIsOpen,
    onBack,
    onConfirm,
    priceChanged,
    oldPrice,
    newPrice,
    deadlineChanged,
    oldCancellationDeadline,
    newCancellationDeadline,
    boardChanged,
    oldBoard,
    newBoard,
    showUnchangedTerms,
    roomName,
    deal,
    nights,
    rooms,
    points,
    pointsBudget,
    profile,
    t,
    i18n,
}) {
    const formatPrice = (price) =>
        Currencies.getPriceWithDisplayCurrencyByPrecision(
            price,
            profile.display_currency_exchange_rate,
            profile.display_currency,
            0,
        );

    const basis = getPriceBasis({ profile, deal, nights, rooms });
    const { show_total_price, toDisplayedPrice } = basis;
    const basisOldPrice = toDisplayedPrice(oldPrice);
    const basisNewPrice = toDisplayedPrice(newPrice);

    const exchangeRate = profile.display_currency_exchange_rate || 1;
    const displayedOld = Math.round(basisOldPrice * exchangeRate);
    const displayedNew = Math.round(basisNewPrice * exchangeRate);
    const isPriceIncrease = priceChanged && displayedNew > displayedOld;
    const displayedDelta = priceChanged ? displayedNew - displayedOld : 0;
    const formatSignedPrice = (delta) =>
        `${delta > 0 ? '+' : '-'}${formatPrice(Math.abs(delta) / exchangeRate)}`;
    const formattedDelta = priceChanged && displayedDelta !== 0 ? formatSignedPrice(displayedDelta) : null;

    const pointsSplit = buildPointsRecheckSplit({
        old_total_price: oldPrice,
        new_total_price: newPrice,
        budget_points: pointsBudget,
        points_ex_rate: points?.points_ex_rate,
    });

    const toDisplayedPoints = (total_points) => Math.round(toDisplayedPrice(total_points) || 0);
    const toDisplayedMoney = (total_money) => Math.round(toDisplayedPrice(total_money) * exchangeRate);
    const displayedOldPoints = pointsSplit.active ? toDisplayedPoints(pointsSplit.old_points) : 0;
    const displayedNewPoints = pointsSplit.active ? toDisplayedPoints(pointsSplit.new_points) : 0;
    const displayedOldMoney = pointsSplit.active ? toDisplayedMoney(pointsSplit.old_money) : 0;
    const displayedNewMoney = pointsSplit.active ? toDisplayedMoney(pointsSplit.new_money) : 0;
    const displayedPointsDelta = priceChanged ? displayedNewPoints - displayedOldPoints : 0;
    const displayedMoneyDelta = priceChanged ? displayedNewMoney - displayedOldMoney : 0;

    const hasVisiblePriceChange =
        priceChanged &&
        (pointsSplit.active ? displayedPointsDelta !== 0 || displayedMoneyDelta !== 0 : displayedDelta !== 0);

    const confirmMoney = pointsSplit.active ? toDisplayedPrice(pointsSplit.new_money) : basisNewPrice;
    const confirmPriceLabel =
        hasVisiblePriceChange && (!pointsSplit.active || displayedNewMoney > 0) ? formatPrice(confirmMoney) : null;

    const priceBasisLabel = !basis.nights
        ? null
        : show_total_price
          ? t('TermsChangedDialog.TotalForNights', { count: basis.nights })
          : t('TermsChangedDialog.PerNight');
    const renderPriceBasis = () =>
        priceBasisLabel ? <span className="terms-changed-price-basis">{priceBasisLabel}</span> : null;

    const newTermsDeadlineTime = newCancellationDeadline ? new Date(newCancellationDeadline).getTime() : NaN;
    const newTermsRefundable = Number.isFinite(newTermsDeadlineTime) && newTermsDeadlineTime > Date.now();
    const disclosesCancellation = !!showUnchangedTerms || !!deadlineChanged;
    const newTermsRefundableForTest = !disclosesCancellation ? 'unknown' : newTermsRefundable ? 'true' : 'false';

    const formatDeadline = (deadline) => {
        const formatted = DateUtils.formatCancellationDeadline(deadline, i18n && i18n.language);
        return formatted
            ? t('TermsChangedDialog.FreeCancelUntil', { date: formatted })
            : t('ReservationDialog.NonRefundable');
    };

    const renderUnchangedValue = (value) => (
        <div className="terms-changed-value-row">
            <span className="terms-changed-value">{value}</span>
            <span className="terms-changed-unchanged-tag">{t('TermsChangedDialog.Unchanged')}</span>
        </div>
    );

    const renderPriceColumn = (displayed_points, basis_money, basis_price) =>
        pointsSplit.active ? (
            <>
                <span className="terms-changed-price-points" data-testid="terms-changed-price-points">
                    <span className="terms-changed-price-points-value">
                        {displayed_points.toLocaleString('en-US')}
                    </span>
                    <span className="terms-changed-price-points-label">{t('Points')}</span>
                </span>
                <span className="terms-changed-price-value" data-testid="terms-changed-price-money">
                    {'+ '}
                    {formatPrice(basis_money)}
                </span>
                {renderPriceBasis()}
            </>
        ) : (
            <>
                <span className="terms-changed-price-value">{formatPrice(basis_price)}</span>
                {renderPriceBasis()}
            </>
        );

    const renderPriceDelta = () =>
        pointsSplit.active ? (
            <span className="terms-changed-delta-group">
                {displayedPointsDelta !== 0 ? (
                    <span
                        className={`terms-changed-delta points ${displayedPointsDelta > 0 ? 'increase' : 'decrease'}`}
                        data-testid="terms-changed-points-delta"
                    >
                        {`${displayedPointsDelta > 0 ? '+' : '-'}${Math.abs(displayedPointsDelta).toLocaleString('en-US')} `}
                        <span className="terms-changed-delta-points-label">{t('Points')}</span>
                    </span>
                ) : null}
                {displayedMoneyDelta !== 0 ? (
                    <span
                        className={`terms-changed-delta money ${displayedMoneyDelta > 0 ? 'increase' : 'decrease'}`}
                        data-testid="terms-changed-money-delta"
                    >
                        {formatSignedPrice(displayedMoneyDelta)}
                    </span>
                ) : null}
            </span>
        ) : (
            <span className={`terms-changed-delta ${isPriceIncrease ? 'increase' : 'decrease'}`}>
                {formattedDelta}
            </span>
        );

    const renderChangedValues = (old_value, new_value) => (
        <div className="terms-changed-deadline-row">
            <div className="terms-changed-deadline-col old">
                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.Old')}</span>
                <span className="terms-changed-deadline-value">{old_value}</span>
            </div>
            <div className="terms-changed-arrow" />
            <div className="terms-changed-deadline-col new">
                <span className="terms-changed-deadline-label">{t('TermsChangedDialog.New')}</span>
                <span className="terms-changed-deadline-value">{new_value}</span>
            </div>
        </div>
    );

    const cards = [
        {
            test_id: 'terms-changed-price',
            changed: hasVisiblePriceChange,
            label: !hasVisiblePriceChange
                ? t('TermsChangedDialog.Price')
                : isPriceIncrease
                  ? t('TermsChangedDialog.PriceIncreased')
                  : t('TermsChangedDialog.PriceDecreased'),
            unchanged_value: renderPriceColumn(
                displayedNewPoints,
                toDisplayedPrice(pointsSplit.new_money),
                basisNewPrice,
            ),
            changed_body: (
                <div className="terms-changed-prices">
                    <div className="terms-changed-price-col old">
                        {renderPriceColumn(displayedOldPoints, toDisplayedPrice(pointsSplit.old_money), basisOldPrice)}
                    </div>
                    <div className="terms-changed-arrow" />
                    <div className="terms-changed-price-new-group">
                        <div className="terms-changed-price-col new">
                            {renderPriceColumn(
                                displayedNewPoints,
                                toDisplayedPrice(pointsSplit.new_money),
                                basisNewPrice,
                            )}
                        </div>
                        {renderPriceDelta()}
                    </div>
                </div>
            ),
        },
        {
            test_id: 'terms-changed-cancellation',
            changed: !!deadlineChanged,
            label: deadlineChanged
                ? t('TermsChangedDialog.CancellationTermsChanged')
                : t('TermsChangedDialog.CancellationTerms'),
            unchanged_value: formatDeadline(newCancellationDeadline),
            changed_body: renderChangedValues(
                formatDeadline(oldCancellationDeadline),
                formatDeadline(newCancellationDeadline),
            ),
        },
        {
            test_id: 'terms-changed-extras',
            changed: !!boardChanged,
            label: boardChanged ? t('TermsChangedDialog.ExtrasChanged') : t('TermsChangedDialog.Extras'),
            unchanged_value: t(newBoard),
            changed_body: renderChangedValues(t(oldBoard), t(newBoard)),
        },
    ];

    // If nothing meaningful changed at display precision, skip the dialog entirely.
    React.useEffect(() => {
        if (modalIsOpen && !hasVisiblePriceChange && !deadlineChanged && !boardChanged) {
            onConfirm();
        }
    }, [modalIsOpen, hasVisiblePriceChange, deadlineChanged, boardChanged]);

    return (
        <Dialog
            className="reservation-dialog-main"
            open={modalIsOpen}
            aria-describedby="terms-changed-dialog-description"
            fullWidth={true}
        >
            <div
                className="terms-changed-container"
                data-testid="terms-changed-dialog"
                data-new-terms-refundable-for-test={newTermsRefundableForTest}
            >
                <div className="terms-changed-header">
                    <h3 className="terms-changed-title">{t('TermsChangedDialog.Title')}</h3>
                    {roomName ? (
                        <div className="terms-changed-room-name" data-testid="terms-changed-room-name">
                            {roomName}
                        </div>
                    ) : null}
                </div>

                <div className="terms-changed-cards">
                    {cards
                        .filter(({ changed }) => showUnchangedTerms || changed)
                        .map(({ test_id, label, changed, changed_body, unchanged_value }) => (
                            <div className="terms-changed-card" key={test_id} data-testid={test_id}>
                                <span className="terms-changed-card-label">{label}</span>
                                {changed ? changed_body : renderUnchangedValue(unchanged_value)}
                            </div>
                        ))}
                </div>

                <div className="terms-changed-actions">
                    <Button
                        className="terms-changed-btn-back"
                        data-testid="terms-changed-back-button"
                        variant="outlined"
                        onClick={onBack}
                    >
                        {t('ReservationDialog.BackToHotelButton')}
                    </Button>
                    <Button
                        className="terms-changed-btn-confirm"
                        data-testid="terms-changed-confirm-button"
                        variant="contained"
                        onClick={onConfirm}
                    >
                        {confirmPriceLabel
                            ? t('TermsChangedDialog.ConfirmContinueWithPrice', { price: confirmPriceLabel })
                            : t('ReservationDialog.ConfirmContinueButton')}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

TermsChangedDialog.propTypes = {
    modalIsOpen: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    priceChanged: PropTypes.bool,
    oldPrice: PropTypes.number,
    newPrice: PropTypes.number,
    deadlineChanged: PropTypes.bool,
    oldCancellationDeadline: PropTypes.string,
    newCancellationDeadline: PropTypes.string,
    boardChanged: PropTypes.bool,
    oldBoard: PropTypes.string,
    newBoard: PropTypes.string,
    showUnchangedTerms: PropTypes.bool,
    roomName: PropTypes.string,
    deal: PropTypes.object,
    nights: PropTypes.number,
    rooms: PropTypes.number,
    points: PropTypes.object,
    pointsBudget: PropTypes.number,
    profile: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.shape({ language: PropTypes.string }),
};

export default memo(withTranslation()(TermsChangedDialog));
